// Price group prices (legacy PricesComponent + ItemPriceComponent):
// GET price_groups/{id}, GET price_groups/{id}/items?page=N (Laravel paginator),
// POST price_groups/update_item_price { price_group_id, item_id, variation_id?, portion_id?, price, taxes[] }
// with a debounced autosave per row.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, CircularProgress, Divider, LinearProgress, MenuItem, Pagination, Paper, Stack, TextField, Typography } from '@mui/material';
import { ArrowLeft2, TickCircle } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchSelectOptions, type SelectOption } from '../../api/client';
import { t } from '../../i18n';
import { asArray, asRecord, metaLabel } from './shared';
import { UI } from '../../brand/ui';

type Paginator = { data: Array<Record<string, unknown>>; current_page: number; last_page: number; total: number; from: number | null; to: number | null };

function pivotOf(entity: Record<string, unknown>) {
  const group = asArray(entity.price_group ?? entity.priceGroup)[0];
  return asRecord(group?.pivot);
}

function parseTaxes(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function PriceGroupPricesPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Record<string, unknown> | null>(null);
  const [taxes, setTaxes] = useState<SelectOption[]>([]);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Paginator | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.get(`price_groups/${id}`).then((response) => setGroup(response.data)).catch((err) => setError(apiErrorMessage(err, t('catalog.prices.loadFailed'))));
    fetchSelectOptions('taxes/search').then(setTaxes).catch(() => setTaxes([]));
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .get(`price_groups/${id}/items`, { params: { page } })
      .then((response) => !cancelled && setItems(response.data as Paginator))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t('catalog.prices.loadFailed'))))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, page]);

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>{t('catalog.prices.title', { name: String(group?.name ?? '') })}</Typography>
            <Typography variant="body2" color="text.secondary">{t('catalog.prices.description')}</Typography>
          </Box>
          <Button startIcon={<ArrowLeft2 size={16} />} onClick={() => navigate('/price-groups')}>{t('catalog.prices.back')}</Button>
        </Stack>
      </Paper>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {loading ? <LinearProgress /> : null}
        <Stack divider={<Divider />}>
          {(items?.data ?? []).map((item) => (
            <Box key={String(item.id)} sx={{ px: 2, py: 1.25 }}>
              <PriceRow groupId={id} item={item} taxes={taxes} label={`${String(item.name ?? '')} (${String(item.code ?? '')})`} />
              {asArray(item.portions).map((portion) => (
                <PriceRow key={String(portion.id)} groupId={id} item={item} portion={portion} taxes={taxes} label={String(portion.name ?? '') === 'regular' ? t('catalog.regular') : String(portion.name ?? '')} nested />
              ))}
              {asArray(item.variations).map((variation) => (
                <PriceRow key={String(variation.id)} groupId={id} item={item} variation={variation} taxes={taxes} label={metaLabel(variation.meta) || String(variation.code ?? '')} nested />
              ))}
            </Box>
          ))}
          {!loading && !items?.data?.length ? <Typography sx={{ p: 3 }} color="text.secondary">{t('catalog.noRecords')}</Typography> : null}
        </Stack>
        {items && items.total > 0 ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2">{t('catalog.prices.range', { from: items.from ?? 0, to: items.to ?? 0, total: items.total })}</Typography>
            <Pagination count={items.last_page} page={items.current_page} onChange={(_event, value) => setPage(value)} />
          </Stack>
        ) : null}
      </Paper>
    </Stack>
  );
}

function PriceRow({
  groupId,
  item,
  portion,
  variation,
  taxes,
  label,
  nested
}: {
  groupId: string;
  item: Record<string, unknown>;
  portion?: Record<string, unknown>;
  variation?: Record<string, unknown>;
  taxes: SelectOption[];
  label: string;
  nested?: boolean;
}) {
  const source = variation ?? portion ?? item;
  const pivot = pivotOf(source);
  const [price, setPrice] = useState<string>(pivot.price != null && pivot.price !== '' ? String(Number(pivot.price)) : '');
  const [selectedTaxes, setSelectedTaxes] = useState<string[]>(parseTaxes(pivot.taxes));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const timer = useRef<number | null>(null);
  const first = useRef(true);

  const save = useCallback(async (nextPrice: string, nextTaxes: string[]) => {
    if (nextPrice === '') return;
    setStatus('saving');
    try {
      const response = await adminApi.post('price_groups/update_item_price', {
        price_group_id: groupId,
        item_id: item.id,
        variation_id: variation?.id,
        portion_id: portion?.id,
        price: Number(nextPrice),
        taxes: nextTaxes
      });
      setStatus(response.data?.success ? 'saved' : 'error');
      if (response.data?.success) window.setTimeout(() => setStatus('idle'), 3000);
    } catch (err) {
      setMessage(apiErrorMessage(err, t('common.saveFailed')));
      setStatus('error');
    }
  }, [groupId, item.id, portion?.id, variation?.id]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(price, selectedTaxes), 500);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [price, selectedTaxes, save]);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ pl: nested ? 3 : 0, py: 0.5 }}>
      <Typography variant="body2" fontWeight={nested ? 500 : 700}>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          type="number"
          placeholder={t('field.price')}
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          sx={{ width: 150 }}
          error={status === 'error'}
          InputProps={{
            endAdornment: status === 'saving' ? <CircularProgress size={16} /> : status === 'saved' ? <TickCircle size={18} color={UI.success} /> : null
          }}
        />
        <TextField
          select
          size="small"
          sx={{ width: 300 }}
          label={t('nav.taxes')}
          value={selectedTaxes}
          SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).map((tax) => taxes.find((option) => String(option.value) === tax)?.label ?? tax).join(', ') }}
          onChange={(event) => setSelectedTaxes(typeof event.target.value === 'string' ? event.target.value.split(',') : (event.target.value as string[]))}
        >
          {taxes.map((tax) => (
            <MenuItem key={String(tax.value)} value={String(tax.value)}>
              <Checkbox size="small" checked={selectedTaxes.includes(String(tax.value))} />
              {tax.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {status === 'error' && message ? <Typography variant="caption" color="error">{message}</Typography> : null}
    </Stack>
  );
}
